// LabelChip —— 圆角 chip 展示标签，颜色以 label.color 作背景，前景色按 WCAG 亮度自动选黑/白
// 亮度启发式：Rec.601 luma < 0.5 → 白字 / 深底；否则 → 深字 / 浅底
// 关闭按钮可选（详情/编辑场景 onRemove 传入）
//
// 中文标签强制 whitespace-nowrap（CLAUDE.md §四：中文短标签也会断行）

import { X } from 'lucide-react';

import { cn } from '../../lib/cn.js';

import type { LabelDTO } from '../../lib/labels.js';

/** LabelChip 只渲染时需要 name/color/isSystem 三字段；createdAt 等运行时元数据不用。
 *  用 Pick 让 MessageLabelSummary（列表页精简 DTO）也能直接传入。 */
export type LabelChipLike = Pick<LabelDTO, 'name' | 'color' | 'isSystem'>;

interface Props {
  label: LabelChipLike;
  onRemove?: () => void;
  size?: 'sm' | 'md';
  className?: string;
}

const SIZE_CLS: Record<NonNullable<Props['size']>, string> = {
  sm: 'text-[10px] leading-4 px-1.5 py-0.5',
  md: 'text-xs leading-5 px-2 py-0.5',
};

export function LabelChip({ label, onRemove, size = 'sm', className }: Props) {
  const dark = isDarkColor(label.color);
  const style = dark
    ? { backgroundColor: label.color, color: '#ffffff' }
    : { backgroundColor: withAlpha(label.color, 0.14), color: label.color };
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full font-medium whitespace-nowrap',
        SIZE_CLS[size],
        className,
      )}
      style={style}
      title={label.isSystem ? `${label.name}（系统预置）` : label.name}
    >
      <span className="truncate max-w-[8rem]">{label.name}</span>
      {onRemove ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          aria-label={`移除标签 ${label.name}`}
          className="touch-target rounded-full p-0.5 opacity-70 hover:opacity-100"
        >
          <X className="h-3 w-3" />
        </button>
      ) : null}
    </span>
  );
}

// ---- 颜色辅助 ----

/** #RRGGBB / #RGB → { r, g, b }（0..255） */
function parseHex(hex: string): { r: number; g: number; b: number } | null {
  const h = hex.trim().replace(/^#/, '');
  if (h.length === 3) {
    const r = h[0];
    const g = h[1];
    const b = h[2];
    if (!r || !g || !b) return null;
    return {
      r: parseInt(r + r, 16),
      g: parseInt(g + g, 16),
      b: parseInt(b + b, 16),
    };
  }
  if (h.length === 6) {
    return {
      r: parseInt(h.slice(0, 2), 16),
      g: parseInt(h.slice(2, 4), 16),
      b: parseInt(h.slice(4, 6), 16),
    };
  }
  return null;
}

function isDarkColor(hex: string): boolean {
  const c = parseHex(hex);
  if (!c) return true; // 解析失败视作深色，白字兜底可读
  // Rec.601 luminance
  const luma = (0.299 * c.r + 0.587 * c.g + 0.114 * c.b) / 255;
  return luma < 0.6;
}

function withAlpha(hex: string, alpha: number): string {
  const c = parseHex(hex);
  if (!c) return hex;
  return `rgba(${String(c.r)}, ${String(c.g)}, ${String(c.b)}, ${String(alpha)})`;
}
